import type {
  ApiKey,
  ContentTypeDefinition,
} from '../../../../lib/flotiq-client/types/api-response';

import { useStore } from '@tanstack/react-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../../../lib/globals';
import { getTestProps } from '../../../../lib/helpers';

import Button from '../../../../components/Button/Button';

import useSelectedSpace from '../../../../hooks/useSelectedSpace';

import { withForm } from '../../../../form-hooks/form';

interface BasicProps {
  /**
   * User api keys
   */
  apiKeys: ApiKey[] | never[];
  /**
   * On generate API key button click
   */
  onGenerateApiKey: () => Promise<ApiKey | null>;
  /**
   * Whether the API key was generated
   */
  apiKeyGenerated: boolean;
  /**
   * Content type for which form is displayed
   */
  contentType: ContentTypeDefinition;
  /**
   * Component test ID
   */
  testId?: string;
}

const Basic = withForm({
  defaultValues: {
    origin: '*',
    flotiqApiKey: '',
    config: {},
    messages: {},
  },
  props: {
    apiKeys: EMPTY_ARRAY,
    onGenerateApiKey: EMPTY_FUNCTION,
    apiKeyGenerated: false,
    contentType: {},
    testId: '',
  } as BasicProps,
  render: function Render({
    form,
    apiKeys,
    onGenerateApiKey,
    apiKeyGenerated,
    contentType,
    testId,
  }) {
    const { t } = useTranslation();
    const { space, spaceLimits } = useSelectedSpace();

    const [generatingKey, setGeneratingKey] = useState(false);

    const handleGenerate = useCallback(async () => {
      setGeneratingKey(true);
      const newKey = await onGenerateApiKey();

      if (newKey) {
        form.setFieldValue('flotiqApiKey', newKey.apiKey);
        form.validateField('flotiqApiKey', 'change');
      }
      setGeneratingKey(false);
    }, [form, onGenerateApiKey]);

    const apiKeyOptions = useMemo(
      () => apiKeys.map(({ apiKey, name }) => ({ value: apiKey, label: name })),
      [apiKeys],
    );

    const defaultApiKey = useMemo(
      () =>
        apiKeys.find(({ permissions }) =>
          permissions?.find(
            ({ canCreate, canRead, contentTypeDefinition }) =>
              canCreate &&
              canRead &&
              contentTypeDefinition.id === contentType.id,
          ),
        )?.apiKey,
      [apiKeys, contentType.id],
    );

    const flotiqApiKey = useStore(
      form.store,
      (state) => state.values.flotiqApiKey,
    );

    useEffect(() => {
      if (defaultApiKey && !flotiqApiKey) {
        form.setFieldValue('flotiqApiKey', defaultApiKey);
      }
    }, [defaultApiKey, flotiqApiKey, form]);

    const disabledGenerateKey = useMemo(() => {
      const scopedKeysLen = apiKeys.filter((el) => !el.global).length;
      const scopedKeysLimit = spaceLimits?.scoped_keys_limit;
      return scopedKeysLimit !== -1 && scopedKeysLimit <= scopedKeysLen;
    }, [apiKeys, spaceLimits?.scoped_keys_limit]);

    return (
      <div className="space-y-5">
        <form.AppField name="origin">
          {(subField) => (
            <subField.Input
              label={t('ContentTypeForm.FormGenerator.Field.origin')}
              helpText={t('ContentTypeForm.FormGenerator.HelpText.origin')}
              required
              {...getTestProps(testId, 'origin', 'testId')}
            />
          )}
        </form.AppField>

        <form.AppField name="flotiqApiKey">
          {(subField) => (
            <subField.Dropdown
              label={t('ContentTypeForm.FormGenerator.Field.flotiqApiKey')}
              options={apiKeyOptions}
              helpText={
                <>
                  {t('ContentTypeForm.FormGenerator.HelpText.flotiqApiKey')}
                  <div className="flex items-center mt-2 gap-2">
                    <Button
                      onClick={handleGenerate}
                      disabled={
                        apiKeyGenerated || generatingKey || disabledGenerateKey
                      }
                      {...getTestProps(testId, 'generate-api-key', 'testId')}
                    >
                      {t('ContentTypeForm.FormGenerator.ApiKeyGenerate')}
                    </Button>
                    {apiKeyGenerated &&
                      t('ContentTypeForm.FormGenerator.Generated')}
                    {disabledGenerateKey && (
                      <span>
                        <Trans i18nKey="ContentTypeForm.FormGenerator.ApiKeyLimitReached">
                          You&apos;ve reached maximum number of keys in your
                          plan, please select existing key, or
                          <Link
                            className="text-blue"
                            to={`/space/upgrade/${space}`}
                          >
                            upgrade your plan
                          </Link>
                        </Trans>
                      </span>
                    )}
                  </div>
                </>
              }
              disabled={generatingKey}
              required
              {...getTestProps(testId, 'flotiqApiKey', 'testId')}
            />
          )}
        </form.AppField>
      </div>
    );
  },
});

export default Basic;
